import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../api/supabase'
import type { Invoice } from '../../hooks/useInvoices'
import { CREDIT_REASONS } from '../../lib/invoiceRules'
import { fmt } from '../../lib/invoiceFormat'
import InvoiceDialogShell, { ErrorBox, btnCancel, fieldCls, labelCls } from './InvoiceDialogShell'

interface Line { id: string; description: string; amount: number; vat: number; credited: boolean }
type Mode = 'FULL' | 'AMOUNT' | 'LINES'

export default function CreditNoteDialog({ invoice, onClose, onDone }: { invoice: Invoice; onClose: () => void; onDone: () => void }) {
  const [mode, setMode] = useState<Mode>('FULL')
  const [lines, setLines] = useState<Line[]>([])
  const [creditedNet, setCreditedNet] = useState(0)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    (async () => {
      const [{ data: charges }, { data: cns }] = await Promise.all([
        supabase.from('job_charges').select('id, description, sale_amount, sale_vat_rate, charge_code:charge_codes(description)').eq('invoice_id', invoice.id),
        supabase.from('invoices').select('id, amount').eq('credit_note_of_invoice_id', invoice.id).neq('status', 'VOID'),
      ])
      const cnIds = (cns || []).map((c: any) => c.id)
      setCreditedNet((cns || []).reduce((s: number, c: any) => s + (c.amount || 0), 0))
      const { data: cnLines } = cnIds.length
        ? await supabase.from('credit_note_lines').select('source_charge_id').in('credit_note_id', cnIds)
        : { data: [] as { source_charge_id: string | null }[] }
      const done = new Set((cnLines || []).map(l => l.source_charge_id))
      setLines((charges || []).map((c: any) => ({
        id: c.id, description: c.charge_code?.description || c.description || '(charge)',
        amount: c.sale_amount || 0, vat: c.sale_vat_rate || 0, credited: done.has(c.id),
      })))
    })()
  }, [invoice.id])

  const remaining = (invoice.amount || 0) - creditedNet
  const vatRatio = invoice.amount > 0 ? (invoice.vat_amount || 0) / invoice.amount : 0

  const preview = useMemo(() => {
    let net = 0, vat = 0
    if (mode === 'FULL') { net = remaining; vat = remaining * vatRatio }
    else if (mode === 'AMOUNT') { net = parseFloat(amount) || 0; vat = net * vatRatio }
    else for (const l of lines) if (picked.has(l.id)) { net += l.amount; vat += l.amount * l.vat / 100 }
    return { net, vat }
  }, [mode, amount, picked, lines, remaining, vatRatio])

  const hasPartial = creditedNet > 0
  const togglePick = (id: string) => setPicked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  const valid = !!reason && (mode === 'FULL' ? !hasPartial : mode === 'AMOUNT' ? preview.net > 0 && preview.net <= remaining + 0.001 : picked.size > 0)

  const submit = async () => {
    setBusy(true); setError(null)
    const { error } = await supabase.rpc('create_credit_note', {
      p_invoice_id: invoice.id, p_mode: mode,
      p_amount: mode === 'AMOUNT' ? parseFloat(amount) : null,
      p_charge_ids: mode === 'LINES' ? Array.from(picked) : null,
      p_reason: reason, p_notes: notes.trim() || null,
    })
    setBusy(false)
    if (error) { setError(error.message); return }
    onDone()
  }

  const radio = (m: Mode, label: string, disabled = false) => (
    <label className={`flex items-center gap-2 text-sm ${disabled ? 'opacity-50' : ''}`}>
      <input type="radio" checked={mode === m} disabled={disabled} onChange={() => setMode(m)} /> {label}
    </label>
  )

  return (
    <InvoiceDialogShell title={`Create Credit Note against ${invoice.invoice_no} (${fmt(invoice.total_amount || 0, invoice.currency)})`} width="max-w-lg"
      footer={<>
        <button onClick={onClose} className={btnCancel}>Cancel</button>
        <button onClick={submit} disabled={!valid || busy} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {busy ? 'Creating…' : 'Create Credit Note'}
        </button>
      </>}>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        The invoice stays valid; the credit note offsets it. Amounts on the credit note are positive.
        {hasPartial && <> Already credited: <b>{fmt(creditedNet, invoice.currency)}</b> net — <b>{fmt(remaining, invoice.currency)}</b> left.</>}
      </p>
      <div className="space-y-2">
        {radio('FULL', `Full reversal (${fmt(remaining, invoice.currency)} net + VAT)`, hasPartial)}
        {radio('AMOUNT', 'Partial — enter an amount')}
        {radio('LINES', 'Partial — pick charge lines')}
      </div>
      {mode === 'AMOUNT' && (
        <div>
          <label className={labelCls}>Net amount to credit (max {fmt(remaining, invoice.currency)}); VAT is pro-rata</label>
          <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className={fieldCls} />
        </div>
      )}
      {mode === 'LINES' && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
          {lines.map(l => (
            <label key={l.id} className={`flex items-center gap-2 px-3 py-2 text-sm ${l.credited ? 'opacity-50' : ''}`}>
              <input type="checkbox" disabled={l.credited} checked={picked.has(l.id)} onChange={() => togglePick(l.id)} />
              <span className="flex-1 text-gray-800 dark:text-gray-200">{l.description}{l.credited && ' (already credited)'}</span>
              <span className="text-gray-600 dark:text-gray-400">{fmt(l.amount, invoice.currency)}</span>
            </label>
          ))}
        </div>
      )}
      <div className="text-sm bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 flex justify-between">
        <span className="text-gray-600 dark:text-gray-300">Credit note total</span>
        <span className="font-semibold text-gray-900 dark:text-white">
          {fmt(preview.net + preview.vat, invoice.currency)} <span className="font-normal text-xs text-gray-500">({fmt(preview.net, invoice.currency)} + VAT {fmt(preview.vat, invoice.currency)})</span>
        </span>
      </div>
      <div>
        <label className={labelCls}>Reason *</label>
        <select value={reason} onChange={e => setReason(e.target.value)} className={fieldCls}>
          <option value="">Select a reason…</option>
          {CREDIT_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <div>
        <label className={labelCls}>Notes (optional)</label>
        <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} className={fieldCls} />
      </div>
      <ErrorBox message={error} />
    </InvoiceDialogShell>
  )
}
